import React, { useMemo, useState } from 'react';
import { severeWeatherReportService } from '../services/api';

const REPORT_TYPES = [
  { value: 'tornado', label: 'Tornado' },
  { value: 'wind', label: 'Wind damage' },
  { value: 'hail', label: 'Large hail' },
  { value: 'flooding', label: 'Flooding' },
  { value: 'lightning', label: 'Lightning damage' },
  { value: 'other', label: 'Other severe weather' },
];

const DAMAGE_TYPES = [
  'structural damage',
  'roof damage',
  'tree damage',
  'power lines',
  'vehicle damage',
  'crop damage',
  'injury report',
  'other',
];

const ReportWeatherPage: React.FC = () => {
  const [reportType, setReportType] = useState('tornado');
  const [damageType, setDamageType] = useState(DAMAGE_TYPES[0]);
  const [locationDescription, setLocationDescription] = useState('');
  const [occurredAt, setOccurredAt] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [reporterName, setReporterName] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const attachmentSummary = useMemo(
    () => attachments.map((file) => `${file.name} (${Math.round(file.size / 1024)} KB)`),
    [attachments],
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      await severeWeatherReportService.create({
        report_type: reportType,
        damage_type: damageType,
        location_description: locationDescription,
        occurred_at: occurredAt,
        report_details: reportDetails,
        reporter_name: reporterName.trim() || undefined,
        attachments,
      });

      setReportType('tornado');
      setDamageType(DAMAGE_TYPES[0]);
      setLocationDescription('');
      setOccurredAt('');
      setReportDetails('');
      setReporterName('');
      setAttachments([]);
      setSuccess('Your severe weather report has been submitted for staff review.');
    } catch (requestError: any) {
      setError(requestError.response?.data?.error || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-shell">
      <div className="container">
        <div className="page-intro">
          <div>
            <p className="eyebrow">Public reporting</p>
            <h1 className="page-heading">Report severe weather</h1>
            <p className="page-copy">
              Send tornado, wind damage, hail, flooding, or other severe weather reports directly to GLSN staff. At least one attachment is required to help reduce false or spam submissions.
            </p>
          </div>
        </div>

        <div className="card">
          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          <form onSubmit={handleSubmit} className="compose-form" encType="multipart/form-data">
            <div className="form-grid two-column">
              <div className="form-group">
                <label className="form-label" htmlFor="report-type">Report type</label>
                <select id="report-type" className="form-input" value={reportType} onChange={(event) => setReportType(event.target.value)}>
                  {REPORT_TYPES.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="damage-type">Damage type</label>
                <select id="damage-type" className="form-input" value={damageType} onChange={(event) => setDamageType(event.target.value)}>
                  {DAMAGE_TYPES.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-grid two-column">
              <div className="form-group">
                <label className="form-label" htmlFor="report-location">Where did this happen?</label>
                <input
                  id="report-location"
                  className="form-input"
                  value={locationDescription}
                  onChange={(event) => setLocationDescription(event.target.value)}
                  placeholder="Town, county, road, intersection, or landmark"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="report-time">What time did it happen?</label>
                <input
                  id="report-time"
                  type="datetime-local"
                  className="form-input"
                  value={occurredAt}
                  onChange={(event) => setOccurredAt(event.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="report-details">What happened?</label>
              <textarea
                id="report-details"
                className="form-input form-textarea form-textarea-small"
                value={reportDetails}
                onChange={(event) => setReportDetails(event.target.value)}
                placeholder="Describe what you saw, the damage observed, and any other useful context."
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="reporter-name">Your name</label>
              <input
                id="reporter-name"
                className="form-input"
                value={reporterName}
                onChange={(event) => setReporterName(event.target.value)}
                placeholder="Optional, but helpful for verification"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="report-attachments">Attachments</label>
              <input
                id="report-attachments"
                type="file"
                className="form-input"
                multiple
                required
                accept="image/*,video/*,.pdf"
                onChange={(event) => setAttachments(Array.from(event.target.files || []))}
              />
              <p className="field-help">One or more photos, video clips, or PDFs are required. Up to 5 files, 10 MB each.</p>
              {attachmentSummary.length > 0 && (
                <div className="attachment-list">
                  {attachmentSummary.map((item) => <span key={item} className="chip chip-static">{item}</span>)}
                </div>
              )}
            </div>

            <div className="action-row">
              <button type="submit" className="btn btn-primary" disabled={submitting || attachments.length === 0}>
                {submitting ? 'Submitting report...' : 'Submit report'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReportWeatherPage;